import { useRef, useState, type ChangeEvent } from 'react';
import { Box, Button, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { FieldValue } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeForSearch, translateWithCount } from './ValueEditors/valueUtils';

interface StoreListImportProps {
    values: FieldValue[];
    onChange: (value: string[]) => void;
}

// Single source for the file picker filter and the formats shown on the button.
const ACCEPTED_EXTENSIONS = ['.csv', '.txt'];

const firstCellOf = (line: string): string => line.split(/[;,\t|]/)[0].trim();

const codeOf = (value: string): string => value.trim().split(/\s+/)[0];

const normalizedCodeOf = (value: string): string => normalizeForSearch(codeOf(value));

const looksLikeCode = (value: string): boolean => /^\d+$/.test(value);

function matchStores(lines: string[], values: FieldValue[]) {
    const nameByCode = new Map<string, string>();
    values.forEach(option => {
        nameByCode.set(normalizedCodeOf(option.name), option.name);
        if (option.label) nameByCode.set(normalizedCodeOf(option.label), option.name);
    });

    const matched = new Set<string>();
    const unrecognized: string[] = [];
    lines.forEach((line, index) => {
        const matchedName = nameByCode.get(normalizedCodeOf(line));
        if (matchedName) matched.add(matchedName);
        else if (index > 0 || looksLikeCode(codeOf(line))) unrecognized.push(line);
    });
    return { matched: Array.from(matched), unrecognized };
}

function StoreListImport({ values, onChange }: StoreListImportProps) {
    const { t } = useLanguage();
    const inputRef = useRef<HTMLInputElement>(null);
    const [unrecognized, setUnrecognized] = useState<string[]>([]);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (inputRef.current) inputRef.current.value = '';
        if (!file) return;

        const lines = (await file.text()).split(/\r?\n/).map(firstCellOf).filter(Boolean);
        const result = matchStores(lines, values);
        setUnrecognized(result.unrecognized);
        onChange(result.matched);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon fontSize="small" />}
                    onClick={() => inputRef.current?.click()}
                >
                    {t('pages.clients.queryBuilder.simple.importStores.button')} ({ACCEPTED_EXTENSIONS.join(', ')})
                </Button>
                <input ref={inputRef} type="file" accept={ACCEPTED_EXTENSIONS.join(',')} onChange={handleFileChange} style={{ display: 'none' }} />
            </Box>
            {unrecognized.length > 0 && (
                <Alert severity="warning" sx={{ py: 0.5 }} onClose={() => setUnrecognized([])}>
                    {translateWithCount(t, 'pages.clients.queryBuilder.simple.importStores.unrecognized', unrecognized.length)}
                    {': '}
                    {unrecognized.join(', ')}
                </Alert>
            )}
        </Box>
    );
}

export default StoreListImport;
